import inputRuleHandler from "@ext/markdown/elements/list/edit/logic/inputRuleHandler";
import { handleBackspace } from "@ext/markdown/elements/list/edit/logic/keymaps/handleBackspace";
import { handleDelete } from "@ext/markdown/elements/list/edit/logic/keymaps/handleDelete";
import taskListPlugin from "@ext/markdown/elements/list/edit/models/listItem/logic/taskListPlugin";
import { listItem } from "@ext/markdown/elements/list/edit/models/listItem/model/listItemSchema";
import getExtensionOptions from "@ext/markdown/logic/getExtensionOptions";
import { mergeAttributes, wrappingInputRule } from "@tiptap/core";
import { ListItem, type ListItemOptions, TaskItem } from "@tiptap/extension-list";

export const CHECKED_ATTR = "checked";

const taskItemInputRegex = /^\s*(\[([( |x])?\])\s$/;

const listTypes = [
	{
		itemName: "listItem",
		wrapperNames: ["orderedList", "bulletList", "taskList"],
	},
];

const CustomListItem = ListItem.extend<ListItemOptions>({
	...getExtensionOptions({ schema: listItem, name: "listItem" }),

	addOptions(options) {
		return { ...options, simple: options?.simple ?? true, HTMLAttributes: {} };
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"li",
			mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { "data-component": "list-item" }),
			0,
		];
	},

	addNodeView() {
		return (props) => {
			if (props.node.attrs.isTaskItem) return TaskItem.config.addNodeView.call(this)(props);
		};
	},

	addInputRules() {
		return [
			inputRuleHandler(
				wrappingInputRule({
					find: taskItemInputRegex,
					type: this.type,
					getAttributes: (match) => ({
						checked: match[match.length - 1] === "x",
						isTaskItem: true,
					}),
					joinPredicate: (_, node) => node.type === this.type,
				}),
			),
		];
	},

	addProseMirrorPlugins() {
		const parentPlugins = this.parent?.() ?? [];
		const type = this.type.name;
		const schema = this.editor.schema;

		return [...parentPlugins, taskListPlugin(type, schema)];
	},

	addKeyboardShortcuts() {
		return {
			...this.parent(),
			Enter: ({ editor }) => {
				if (editor.isActive("code_block")) return;
				return this.parent().Enter({ editor });
			},
			Delete: ({ editor }) => {
				let handled = false;

				listTypes.forEach(({ itemName }) => {
					if (editor.state.schema.nodes[itemName] === undefined) {
						return;
					}

					if (handleDelete(editor, itemName)) {
						handled = true;
					}
				});

				return handled;
			},
			"Mod-Delete": ({ editor }) => {
				let handled = false;

				listTypes.forEach(({ itemName }) => {
					if (editor.state.schema.nodes[itemName] === undefined) {
						return;
					}

					if (handleDelete(editor, itemName)) {
						handled = true;
					}
				});

				return handled;
			},
			Backspace: ({ editor }) => {
				let handled = false;

				listTypes.forEach(({ itemName, wrapperNames }) => {
					if (editor.state.schema.nodes[itemName] === undefined) {
						return;
					}

					if (handleBackspace(editor, itemName, wrapperNames)) {
						handled = true;
					}
				});

				return handled;
			},
			"Mod-Backspace": ({ editor }) => {
				let handled = false;

				listTypes.forEach(({ itemName, wrapperNames }) => {
					if (editor.state.schema.nodes[itemName] === undefined) {
						return;
					}

					if (handleBackspace(editor, itemName, wrapperNames)) {
						handled = true;
					}
				});

				return handled;
			},
		};
	},
});

export default CustomListItem;
